import HttpMethod from '@/lib/constants/HttpMethod.enum';
import googleDriveApiClient from './googleDriveApiClient';
import AbstractLargeFileUploader from '../../utils/upload/LargeFileUploader/AbstractLargeFileUploader';
import LargeFileUploadError from '../../utils/upload/LargeFileUploader/LargeFileUploadError';
import { type GoogleDriveUploadResponse } from '.';

export default class GoogleDriveLargeFileUploader extends AbstractLargeFileUploader<GoogleDriveUploadResponse> {
  // About 10 MB. Must be a multiple of 256 KB (256 * 1024 bytes) for Google
  // Drive upload to work
  protected readonly CHUNK_SIZE = 40 * 256 * 1024;

  protected async startUploadSession(file: File): Promise<string> {
    const uploadSessionResponse =
      await googleDriveApiClient.fetchRawApiResponse(
        '/upload/drive/v3/files?uploadType=resumable',
        {
          method: HttpMethod.POST,
          headers: {
            'X-Upload-Content-Type': file.type,
            'X-Upload-Content-Length': file.size.toString(),
            'Content-Type': 'application/json; charset=UTF-8',
          },
          body: JSON.stringify({
            name: file.name,
            mimeType: file.type,
          }),
        },
      );

    if (uploadSessionResponse.status !== 200) {
      throw new LargeFileUploadError();
    }

    const uploadChunkApiUrl = uploadSessionResponse.headers.get('Location');

    if (uploadChunkApiUrl === null) {
      throw new LargeFileUploadError();
    }

    return uploadChunkApiUrl;
  }

  protected async uploadFileChunk(
    uploadChunkApiUrl: string,
    fileChunk: Blob,
    chunkByteRange: [number, number],
    totalFileSize: number,
  ): Promise<[false, unknown] | [true, GoogleDriveUploadResponse]> {
    const uploadResponse = await googleDriveApiClient.fetchRawApiResponse(
      uploadChunkApiUrl,
      {
        method: HttpMethod.PUT,
        headers: this.getStandardUploadChunkRequestHeaders(
          fileChunk,
          chunkByteRange,
          totalFileSize,
        ),
        body: fileChunk,
      },
    );

    if (![200, 201].includes(uploadResponse.status)) {
      return [false, null as unknown];
    }

    const uploadResponseContent = (await uploadResponse.json()) as
      GoogleDriveUploadResponse | unknown;

    if (
      typeof uploadResponseContent !== 'object' ||
      uploadResponseContent === null ||
      !('id' in uploadResponseContent) ||
      typeof uploadResponseContent.id !== 'string'
    ) {
      return [false, null as unknown];
    }

    return [true, uploadResponseContent as GoogleDriveUploadResponse];
  }
}
